import { SOUND } from '../config/GameConfig';

interface ToneOpts {
  type: OscillatorType;
  startFreq: number;
  endFreq?: number;
  dur: number;
  peak?: number;
  attack?: number;
}

/**
 * SoundManager —— 用 WebAudio 过程化合成音效，无需外部 wav/mp3 资源
 */
export default class SoundManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private _enabled: boolean = SOUND.enabled;
  private _lastShotAt = 0;

  constructor() {
    this._init();
  }

  private _init(): void {
    if (!this._enabled) return;
    const Ctx: typeof AudioContext | undefined =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) {
      this._enabled = false;
      return;
    }
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.master.gain.value = SOUND.masterVolume;
    this.master.connect(this.ctx.destination);
  }

  setEnabled(on: boolean): void { this._enabled = on; }
  isEnabled(): boolean { return this._enabled; }

  private _ensureRunning(): boolean {
    if (!this._enabled || !this.ctx || !this.master) return false;
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return true;
  }

  private _tone(opts: ToneOpts): void {
    if (!this._ensureRunning() || !this.ctx || !this.master) return;
    const { type, startFreq, endFreq, dur, peak = 0.6, attack = 0.005 } = opts;
    const t0 = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(startFreq, t0);
    if (endFreq != null) {
      osc.frequency.linearRampToValueAtTime(endFreq, t0 + dur);
    }
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(peak, t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  private _noise(dur: number, peak = 0.5): void {
    if (!this._ensureRunning() || !this.ctx || !this.master) return;
    const sr = this.ctx.sampleRate;
    const len = Math.floor(sr * dur);
    const buf = this.ctx.createBuffer(1, len, sr);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      ch[i] = (Math.random() * 2 - 1) * (1 - i / len);
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const g = this.ctx.createGain();
    g.gain.value = peak;
    src.connect(g);
    g.connect(this.master);
    src.start();
  }

  shoot(): void {
    if (!this._enabled) return;
    const now = performance.now();
    if (now - this._lastShotAt < 55) return;
    this._lastShotAt = now;
    this._tone({ type: 'square', startFreq: 920, endFreq: 720, dur: 0.06, peak: 0.18 });
  }
  hit(): void {
    this._noise(0.05, 0.35);
    this._tone({ type: 'square', startFreq: 240, endFreq: 80, dur: 0.06, peak: 0.18 });
  }
  kill(): void {
    this._tone({ type: 'sine', startFreq: 520, endFreq: 180, dur: 0.22, peak: 0.4 });
    this._tone({ type: 'square', startFreq: 60, endFreq: 30, dur: 0.18, peak: 0.25 });
  }
  bossKill(): void {
    this._noise(0.4, 0.6);
    this._tone({ type: 'sine', startFreq: 180, endFreq: 30, dur: 0.6, peak: 0.55 });
    this._tone({ type: 'sawtooth', startFreq: 90, endFreq: 25, dur: 0.6, peak: 0.35 });
  }
  coin(): void {
    this._tone({ type: 'sine', startFreq: 880, dur: 0.06, peak: 0.4 });
    setTimeout(() => this._tone({ type: 'sine', startFreq: 1320, dur: 0.08, peak: 0.4 }), 50);
  }
  bossArrive(): void {
    this._tone({ type: 'sawtooth', startFreq: 90, endFreq: 60, dur: 0.5, peak: 0.5 });
    setTimeout(() => this._tone({ type: 'sawtooth', startFreq: 120, endFreq: 80, dur: 0.5, peak: 0.5 }), 150);
  }
  upgrade(): void {
    this._tone({ type: 'sine', startFreq: 660, dur: 0.1, peak: 0.4 });
    setTimeout(() => this._tone({ type: 'sine', startFreq: 880, dur: 0.1, peak: 0.4 }), 80);
    setTimeout(() => this._tone({ type: 'sine', startFreq: 1320, dur: 0.16, peak: 0.45 }), 160);
  }
  hurt(): void {
    this._tone({ type: 'sawtooth', startFreq: 240, endFreq: 80, dur: 0.18, peak: 0.5 });
    this._noise(0.06, 0.35);
  }
  click(): void {
    this._tone({ type: 'triangle', startFreq: 700, endFreq: 500, dur: 0.05, peak: 0.25 });
  }
  shieldBreak(): void {
    this._tone({ type: 'square', startFreq: 1200, endFreq: 200, dur: 0.25, peak: 0.5 });
    this._noise(0.1, 0.35);
  }
}
